import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DraftIcon from '@mui/icons-material/EditNote';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  doc, getDoc, getDocs, collection, collectionGroup,
  query, where, orderBy, limit, Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth, UserRole } from '../context/AuthContext';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ProfileUser {
  name: string;
  bio: string;
  role: UserRole;
  createdAt: Timestamp | null;
  photoURL?: string;
}

interface PostSummary {
  id: string;
  title: string;
  excerpt: string;
  authorId: string;
  createdAt: Timestamp | null;
  published: boolean;
}

interface CommentItem {
  id: string;
  content: string;
  postId: string;
  postTitle: string;
  createdAt: Timestamp | null;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const ROLE_COLORS: Record<UserRole, 'error' | 'warning' | 'default'> = {
  admin: 'error',
  author: 'warning',
  member: 'default',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(ts: Timestamp | null): string {
  if (!ts) return '';
  return ts.toDate().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return text.slice(0, max).trimEnd() + '…';
}

// ─── Skeleton components ──────────────────────────────────────────────────────

function HeaderSkeleton() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Skeleton variant="circular" width={80} height={80} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="40%" height={36} />
          <Skeleton variant="text" width={100} height={28} />
          <Skeleton variant="text" width={160} />
        </Box>
      </Box>
      <Skeleton variant="text" width="70%" />
      <Skeleton variant="text" width="50%" />
    </Box>
  );
}

function CardSkeleton() {
  return (
    <Card variant="outlined">
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Skeleton variant="text" width="60%" height={28} />
        <Skeleton variant="text" width="100%" />
        <Skeleton variant="text" width="40%" />
      </CardContent>
    </Card>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function UserProfilePage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { authUser, currentUser } = useAuth();

  const [profileUser, setProfileUser] = React.useState<ProfileUser | null>(null);
  const [headerLoading, setHeaderLoading] = React.useState(true);
  const [headerError, setHeaderError] = React.useState('');

  const [tab, setTab] = React.useState<'posts' | 'comments'>('posts');

  const [posts, setPosts] = React.useState<PostSummary[]>([]);
  const [postsLoading, setPostsLoading] = React.useState(true);
  const [postsError, setPostsError] = React.useState('');

  const [comments, setComments] = React.useState<CommentItem[]>([]);
  const [commentsLoading, setCommentsLoading] = React.useState(true);
  const [commentsError, setCommentsError] = React.useState('');
  const [commentsFetched, setCommentsFetched] = React.useState(false);

  // ── Fetch profile user ────────────────────────────────────────────────────

  React.useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    async function loadUser() {
      setHeaderLoading(true);
      setHeaderError('');
      try {
        const snap = await getDoc(doc(db, 'users', userId!));
        if (cancelled) return;
        if (!snap.exists()) {
          setHeaderError('User not found.');
          return;
        }
        const data = snap.data();
        setProfileUser({
          name: data.name ?? '(no name)',
          bio: data.bio ?? '',
          role: (data.role as UserRole) ?? 'member',
          createdAt: data.createdAt ?? null,
          photoURL: data.photoURL,
        });
      } catch (err) {
        console.error('UserProfilePage loadUser:', err);
        if (!cancelled) setHeaderError('Failed to load user profile.');
      } finally {
        if (!cancelled) setHeaderLoading(false);
      }
    }

    loadUser();
    return () => { cancelled = true; };
  }, [userId, authUser?.uid]);

  // ── Fetch posts ───────────────────────────────────────────────────────────

  React.useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    const canSeeDrafts =
      currentUser?.role === 'admin' || authUser?.uid === userId;

    async function loadPosts() {
      setPostsLoading(true);
      setPostsError('');
      try {
        const q = query(
          collection(db, 'posts'),
          where('authorId', '==', userId),
          orderBy('createdAt', 'desc'),
        );
        const snap = await getDocs(q);
        if (cancelled) return;

        const all: PostSummary[] = snap.docs.map(d => {
          const data = d.data();
          return {
            id: d.id,
            title: data.title ?? '(untitled)',
            excerpt: data.excerpt ?? '',
            authorId: data.authorId ?? '',
            createdAt: data.createdAt ?? null,
            published: data.published ?? false,
          };
        });

        setPosts(canSeeDrafts ? all : all.filter(p => p.published));
      } catch (err) {
        console.error('UserProfilePage loadPosts:', err);
        if (!cancelled) setPostsError('Failed to load posts.');
      } finally {
        if (!cancelled) setPostsLoading(false);
      }
    }

    loadPosts();
    return () => { cancelled = true; };
  }, [userId, authUser?.uid, currentUser?.role]);

  // ── Fetch comments (lazy — only when tab first selected) ─────────────────

  React.useEffect(() => {
    if (tab !== 'comments' || commentsFetched || !userId) return;
    let cancelled = false;

    async function loadComments() {
      setCommentsLoading(true);
      setCommentsError('');
      try {
        const q = query(
          collectionGroup(db, 'comments'),
          where('authorId', '==', userId),
          orderBy('createdAt', 'desc'),
          limit(30),
        );
        const snap = await getDocs(q);
        if (cancelled) return;

        // Extract postId from ref path, batch-fetch titles
        const raw = snap.docs.map(d => ({
          id: d.id,
          content: d.data().content as string,
          postId: d.ref.parent.parent!.id,
          createdAt: (d.data().createdAt as Timestamp) ?? null,
        }));

        // Unique post IDs
        const postIds = [...new Set(raw.map(r => r.postId))];
        const titleMap: Record<string, string> = {};
        await Promise.all(
          postIds.map(async pid => {
            try {
              const postSnap = await getDoc(doc(db, 'posts', pid));
              titleMap[pid] = postSnap.exists()
                ? (postSnap.data().title ?? '(untitled)')
                : '(deleted post)';
            } catch {
              titleMap[pid] = '(unknown post)';
            }
          })
        );

        if (cancelled) return;
        setComments(
          raw.map(r => ({
            ...r,
            postTitle: titleMap[r.postId] ?? '(unknown post)',
          }))
        );
        setCommentsFetched(true);
      } catch {
        if (!cancelled) setCommentsError('Failed to load comments.');
      } finally {
        if (!cancelled) setCommentsLoading(false);
      }
    }

    loadComments();
    return () => { cancelled = true; };
  }, [tab, commentsFetched, userId]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ px: { xs: 2, sm: 4 }, py: { xs: 2, sm: 4 }, maxWidth: 800, mx: 'auto' }}>
      {/* Back button */}
      <Button
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 3 }}
        color="inherit"
        onClick={() => navigate(-1)}
      >
        Back
      </Button>

      {/* Header */}
      {headerError ? (
        <Alert severity="error">{headerError}</Alert>
      ) : headerLoading ? (
        <HeaderSkeleton />
      ) : profileUser ? (
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Avatar
              src={profileUser.photoURL}
              sx={{ width: 80, height: 80, fontSize: '1.75rem' }}
            >
              {profileUser.name[0]?.toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {profileUser.name}
              </Typography>
              <Chip
                label={profileUser.role}
                color={ROLE_COLORS[profileUser.role]}
                size="small"
                sx={{ mt: 0.5 }}
              />
              {profileUser.createdAt && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Member since {formatDate(profileUser.createdAt)}
                </Typography>
              )}
            </Box>
          </Box>
          {profileUser.bio && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              {profileUser.bio}
            </Typography>
          )}
        </Box>
      ) : null}

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          label={postsLoading ? 'Posts' : `Posts (${posts.length})`}
          value="posts"
        />
        <Tab
          label={
            !commentsFetched
              ? 'Comments'
              : `Comments (${comments.length})`
          }
          value="comments"
        />
      </Tabs>

      {/* Posts tab */}
      {tab === 'posts' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {postsError && <Alert severity="error">{postsError}</Alert>}
          {postsLoading
            ? Array.from({ length: 3 }).map((_, i) => <CardSkeleton key={i} />)
            : posts.length === 0
              ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Typography color="text.secondary">No posts yet.</Typography>
                </Box>
              )
              : posts.map(post => (
                <Card
                  key={post.id}
                  variant="outlined"
                  sx={!post.published ? { borderStyle: 'dashed' } : undefined}
                >
                  <CardActionArea component={RouterLink} to={`/blog/${post.id}`}>
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                          {post.title}
                        </Typography>
                        {!post.published && (
                          <Chip
                            icon={<DraftIcon />}
                            label="Draft"
                            size="small"
                            variant="outlined"
                            color="warning"
                          />
                        )}
                      </Box>
                      {post.excerpt && (
                        <Typography variant="body2" color="text.secondary">
                          {post.excerpt}
                        </Typography>
                      )}
                      {post.createdAt && (
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(post.createdAt)}
                        </Typography>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))
          }
        </Box>
      )}

      {/* Comments tab */}
      {tab === 'comments' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {commentsError && <Alert severity="error">{commentsError}</Alert>}
          {commentsLoading
            ? Array.from({ length: 3 }).map((_, i) => <CardSkeleton key={i} />)
            : comments.length === 0
              ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Typography color="text.secondary">No comments yet.</Typography>
                </Box>
              )
              : comments.map(c => (
                <Card key={c.id} variant="outlined">
                  <CardActionArea component={RouterLink} to={`/blog/${c.postId}`}>
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {truncate(c.content, 200)}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                        {c.createdAt && (
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(c.createdAt)}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" aria-hidden>·</Typography>
                        <Typography variant="caption" color="text.secondary">
                          on <strong>{c.postTitle}</strong>
                        </Typography>
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))
          }
        </Box>
      )}
    </Box>
  );
}
